<script setup lang="ts">

import { ref, computed, watch } from 'vue';
import BaseModal from '@/application/components/BaseModal.vue';
import AiUpload from '@/upload/components/AiUpload.vue';
import CreateContract from '@/contracts/components/CreateContract.vue';
import { DOCUMENT_EXTRACTION_STEP, useAiDocumentExtractionStore } from '@/documents/store/aiDocumentExtractionStore.ts';
import { storeToRefs } from 'pinia';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { TrackEventCategory } from '@/application/utils/tracking/events.ts';
import LoadSpinner from '@/application/components/LoadSpinner.vue';
import type { ContractSuggestionData } from '@/api/handlungen/types';

const { skipAiUpload, baseHeight = '92%', contractSuggestionData = undefined } = defineProps<{
  show: boolean,
  skipAiUpload?: boolean,
  baseHeight?: string
  contractSuggestionData?: ContractSuggestionData,
}>();
const emit = defineEmits(['close', 'created']);

const showAiUpload = ref(true);
const finished = ref(false);
const aiDocumentExtractionStore = useAiDocumentExtractionStore();
const { uploadedFiles, step, aiIsAvailable, aiCheckIsLoading } = storeToRefs(aiDocumentExtractionStore);

function handleClose() {
  if (step.value === DOCUMENT_EXTRACTION_STEP.EXTRACT) {
    aiDocumentExtractionStore.cancelExtraction();
    useTagManager().push({ event: TrackEventCategory.AI_EXTRACTION_CANCEL_WHILE_EXTRACT });
  }
  emit('close');
}

function handleClosed() {
  aiDocumentExtractionStore.$reset();
  showAiUpload.value = true;
}

const height = computed(() => {
  const aiUploadHeight = uploadedFiles.value.size > 0 ? '620' : '500';
  return !skipAiUpload && (showAiUpload.value || finished.value || aiCheckIsLoading.value) ? aiUploadHeight : baseHeight;
})

function onFinished() {
  finished.value = true
  emit('created')
}

watch(() => aiIsAvailable.value, (newVal) => showAiUpload.value = newVal);
</script>

<template>
  <BaseModal
    :show="show"
    :height="height"
    title="Neuen Vertrag anlegen"
    @close="handleClose"
    @closed="handleClosed"
  >
    <LoadSpinner v-if="aiCheckIsLoading" />
    <AiUpload
      v-else-if="showAiUpload && !skipAiUpload"
      @close="showAiUpload = false"
    />
    <CreateContract
      v-else
      :show-ai-nav="!skipAiUpload"
      :contract-suggestion-data="contractSuggestionData"
      @close="handleClose"
      @finished="onFinished"
      @show-upload="() => {
        showAiUpload = true;
        finished = false;
      }"
    />
  </BaseModal>
</template>
